import { ISchedule, IScheduleEntry } from '@app-types/websocket/schedule.types';
import { IScheduleChange } from '@app-types/websocket/suggestion.types';
import { SUGGESTION_CHANGE_TYPES } from '@constants/suggestionTypes';
import { IDateRange } from '@containers/calendar/calendar.helpter';
import { addDays } from '@helpers/DateHelper';
import { IHandoverData, IHandoverDayData } from './calendar.types';
import { generateHandoverData } from './helper';

const handoverChanged = (original:IHandoverData, suggested:IHandoverData):boolean => {
  return original.atSchool !== suggested.atSchool
    || original.time !== suggested.time
    || original.location !== suggested.location;
}

// the time of a handover at school is not shown, so only a change of time and place outside school is visible
const handoverDisplayChanged = (original:IHandoverData, suggested:IHandoverData):boolean => {
  return original.atSchool !== suggested.atSchool
    || (!original.atSchool && (original.time !== suggested.time || original.location !== suggested.location));
}

const handoverMatches = (first:IHandoverData, second:IHandoverData):boolean => {
  return first.atSchool === second.atSchool
    && first.time === second.time
    && first.location === second.location;
}

const isDayVisit = (data:IHandoverDayData):boolean => {
  return data.handovers.length === 2 && !data.hasNight;
}

const hasDayVisitChange = (original:IHandoverDayData, suggested:IHandoverDayData):boolean => {
  const originalDayVisit = isDayVisit(original);
  const suggestedDayVisit = isDayVisit(suggested);

  if (!originalDayVisit && !suggestedDayVisit) {
    return false;
  }

  if (originalDayVisit !== suggestedDayVisit || original.handovers.length !== suggested.handovers.length) {
    return true;
  }

  return handoverChanged(original.handovers[0], suggested.handovers[0])
    || handoverChanged(original.handovers[1], suggested.handovers[1]);
}

const isCompanionHandoverToDayVisitChangeForRange = (originalEntries:{[key: string]: IScheduleEntry},
  suggestedEntries:{[key: string]: IScheduleEntry}, parent:number, start:number, end:number,
  originalHandover:IHandoverData, suggestedHandover:IHandoverData):boolean => {
  const originalDay = generateHandoverData(originalEntries, parent, start, end);
  const suggestedDay = generateHandoverData(suggestedEntries, parent, start, end);

  if (!hasDayVisitChange(originalDay, suggestedDay)) {
    return false;
  }

  for (let index = 0; index < 2; index++) {
    const originalDayVisitHandover = originalDay.handovers[index];
    const suggestedDayVisitHandover = suggestedDay.handovers[index];

    if (originalDayVisitHandover !== undefined
      && suggestedDayVisitHandover !== undefined
      && handoverMatches(originalHandover, originalDayVisitHandover)
      && handoverMatches(suggestedHandover, suggestedDayVisitHandover)) {
      return true;
    }
  }

  return false;
}

// "CONTINUE" means the period runs on from (or into) a day without a handover
type IChangeHandover = IHandoverData|null|"CONTINUE";

interface ITakesOverChange {
  type:"TAKES_OVER";
  parent:number;
  from:number;
  to:number;
  fromHandover:IChangeHandover;
  toHandover:IChangeHandover;
  time:number;
}

interface IDayVisitChange {
  type:"DAY_VISIT";
  parent:number;
  from:IHandoverData;
  to:IHandoverData;
  oldFrom?:IHandoverData;
  oldTo?:IHandoverData;
  time:number;
}

interface IDayVisitRemovedChange {
  type:"DAY_VISIT_REMOVED";
  parent:number;
  from:IHandoverData;
  to:IHandoverData;
  time:number;
}

interface IHandoverChange {
  type:"HANDOVER_CHANGED";
  from:IHandoverData;
  to:IHandoverData;
  fromParent:number; // the parent the children leave in the handover
  toParent:number;
  time:number;
}

type IScheduleChangeDescription = ITakesOverChange|IDayVisitChange|IDayVisitRemovedChange|IHandoverChange;

const isHandover = (handover:IChangeHandover):handover is IHandoverData => {
  return handover !== null && handover !== "CONTINUE";
}

// in a pickup the parent takes the children over from the other parent, otherwise the parent hands them over
const describeHandoverChange = (from:IHandoverData, to:IHandoverData, parent:number, otherParent:number,
  time:number):IHandoverChange => ({
  type:"HANDOVER_CHANGED",
  from,
  to,
  fromParent:to.pickup ? otherParent : parent,
  toParent:to.pickup ? parent : otherParent,
  time,
});

const describeTakeOver = (parent:number, otherParent:number, from:number, to:number, fromHandover:IChangeHandover,
  toHandover:IChangeHandover, originalAtStart:IHandoverData[], originalAtEnd:IHandoverData[], time:number):IScheduleChangeDescription => {
  // A handover moved to another day looks like a period that starts with the new handover and runs into the day
  // the original handover was on (or the other way around). Show it as the handover being moved.
  if (isHandover(fromHandover) && toHandover === "CONTINUE" && originalAtEnd.length === 1) {
    return describeHandoverChange(originalAtEnd[0], fromHandover, parent, otherParent, time);
  }
  if (fromHandover === "CONTINUE" && isHandover(toHandover) && originalAtStart.length === 1) {
    return describeHandoverChange(originalAtStart[0], toHandover, parent, otherParent, time);
  }
  return {type:"TAKES_OVER", parent, from, to, fromHandover, toHandover, time};
}

// walks the range day by day and describes how the days of one parent change
const describeParentChanges = (originalEntries:{[key: string]: IScheduleEntry}, suggestedEntries:{[key: string]: IScheduleEntry},
  originalOtherEntries:{[key: string]: IScheduleEntry}, suggestedOtherEntries:{[key: string]: IScheduleEntry},
  parent:number, otherParent:number, range:IDateRange):IScheduleChangeDescription[] => {

  const changes:IScheduleChangeDescription[] = [];

  let spinDate = new Date(range.start);
  let start = range.start;
  let end = range.start;

  let takesOverFrom:number|null = null;
  let takesOverHandover:IChangeHandover = null;
  let originalAtTakeOver:IHandoverData[] = [];

  while (spinDate.getTime() < range.end) {
    spinDate = addDays(spinDate, 1);
    end = spinDate.getTime() - 1;
    const time = spinDate.getTime();
    const middleOfDay = Math.round((start+end) / 2);

    const original = generateHandoverData(originalEntries, parent, start, end);
    const suggestion = generateHandoverData(suggestedEntries, parent, start, end);

    if (original.hasNight !== suggestion.hasNight) { // a change in sleeping takes place
      if (suggestion.hasNight) {
        if (takesOverFrom === null) { // this is the first night the parent takes over responsibility
          takesOverFrom = suggestion.handovers.length > 0 ? suggestion.handovers[0].time : middleOfDay;
          takesOverHandover = suggestion.handovers.length > 0 ? suggestion.handovers[0] : "CONTINUE";
          originalAtTakeOver = original.handovers;
        } // else : the parent has already taken over responsibility another night
      } else if (takesOverFrom !== null) { // the other parent takes over again
        const to = suggestion.handovers.length > 0 ? suggestion.handovers[0].time : middleOfDay;
        const toHandover = suggestion.handovers.length > 0 ? suggestion.handovers[0] : null;
        changes.push(describeTakeOver(parent, otherParent, takesOverFrom, to, takesOverHandover, toHandover,
          originalAtTakeOver, original.handovers, time));
        takesOverFrom = null;
      }
    } else if (original.handovers.length !== suggestion.handovers.length) { // a day visit or the end of taking over
      if (takesOverFrom !== null) { // the end of taking over responsibility
        const to = suggestion.handovers.length > 0 ? suggestion.handovers[0].time : middleOfDay;
        const toHandover = suggestion.handovers.length > 0 ? suggestion.handovers[0] : "CONTINUE";
        changes.push(describeTakeOver(parent, otherParent, takesOverFrom, to, takesOverHandover, toHandover,
          originalAtTakeOver, original.handovers, time));
        takesOverFrom = null;
      } else if (isDayVisit(suggestion)) {
        changes.push({type:"DAY_VISIT", parent, from:suggestion.handovers[0], to:suggestion.handovers[1], time});
      } else if (original.handovers.length === 2 && !suggestion.hasNight) { // a day visit was removed
        changes.push({type:"DAY_VISIT_REMOVED", parent, from:original.handovers[0], to:original.handovers[1], time});
      }
    } else if (isDayVisit(original) && isDayVisit(suggestion)) { // a day visit with a new time or place
      if (hasDayVisitChange(original, suggestion)) {
        changes.push({type:"DAY_VISIT", parent, from:suggestion.handovers[0], to:suggestion.handovers[1],
          oldFrom:original.handovers[0], oldTo:original.handovers[1], time});
      }
    } else if (original.handovers.length > 0 && suggestion.handovers.length > 0) { // a change in time and place
      if (takesOverFrom !== null) {
        changes.push(describeTakeOver(parent, otherParent, takesOverFrom, suggestion.handovers[0].time, takesOverHandover,
          suggestion.handovers[0], originalAtTakeOver, original.handovers, time));
        takesOverFrom = null;
      } else if (suggestion.hasNight
        && handoverDisplayChanged(original.handovers[0], suggestion.handovers[0])
        && !isCompanionHandoverToDayVisitChangeForRange(originalOtherEntries, suggestedOtherEntries, otherParent,
          start, end, original.handovers[0], suggestion.handovers[0])) {
        changes.push(describeHandoverChange(original.handovers[0], suggestion.handovers[0], parent, otherParent, time));
      }
    }
    start = spinDate.getTime();
  }
  return changes;
}

// describes the changes between two versions of a schedule in a range of whole days
const describeScheduleChanges = (originalSchedule:ISchedule, suggestedSchedule:ISchedule,
  range:IDateRange):IScheduleChangeDescription[] => {
  const {parentA, parentB} = originalSchedule.data;
  const originalEntriesA = originalSchedule.data.scheduleEntriesParentA;
  const originalEntriesB = originalSchedule.data.scheduleEntriesParentB;
  const suggestedEntriesA = (parentA === suggestedSchedule.data.parentA)
    ? suggestedSchedule.data.scheduleEntriesParentA
    : suggestedSchedule.data.scheduleEntriesParentB;
  const suggestedEntriesB = (parentB === suggestedSchedule.data.parentB)
    ? suggestedSchedule.data.scheduleEntriesParentB
    : suggestedSchedule.data.scheduleEntriesParentA;

  return describeParentChanges(originalEntriesA, suggestedEntriesA, originalEntriesB, suggestedEntriesB, parentA, parentB, range)
    .concat(describeParentChanges(originalEntriesB, suggestedEntriesB, originalEntriesA, suggestedEntriesA, parentB, parentA, range));
}

const withoutEntries = (schedule:ISchedule):ISchedule => ({
  ...schedule,
  data:{...schedule.data, scheduleEntriesParentA:{}, scheduleEntriesParentB:{}},
});

// Builds the entries a suggestion changes as they are before and after it. Entries it leaves alone are the same in both,
// so they are not needed to compare them. Returns undefined if a change is missing the entries it changes.
const createSchedulesFromChanges = (schedule:ISchedule, changes:IScheduleChange[])
  :{original:ISchedule, suggested:ISchedule}|undefined => {
  const original = withoutEntries(schedule);
  const suggested = withoutEntries(schedule);
  let key = 0;

  for (const change of changes) {
    if (change.scheduleId !== schedule.id) {continue;}
    const usesOld = change.type === SUGGESTION_CHANGE_TYPES.UPDATE_SCHEDULE_ENTRY
      || change.type === SUGGESTION_CHANGE_TYPES.REMOVE_SCHEDULE_ENTRY;
    const usesNew = change.type === SUGGESTION_CHANGE_TYPES.UPDATE_SCHEDULE_ENTRY
      || change.type === SUGGESTION_CHANGE_TYPES.ADD_SCHEDULE_ENTRY;
    if ((usesOld && change.oldScheduleEntry === undefined) || (usesNew && change.newScheduleEntry === undefined)) {
      return undefined;
    }
    // entry ids are only unique within one version of a side, so the entries get keys of their own
    if (usesOld && change.oldScheduleEntry) {
      const side = change.isParentA ? original.data.scheduleEntriesParentA : original.data.scheduleEntriesParentB;
      side[key++] = change.oldScheduleEntry;
    }
    if (usesNew && change.newScheduleEntry) {
      const side = change.isParentA ? suggested.data.scheduleEntriesParentA : suggested.data.scheduleEntriesParentB;
      side[key++] = change.newScheduleEntry;
    }
  }
  return {original, suggested};
}

export { describeScheduleChanges, createSchedulesFromChanges, IScheduleChangeDescription, IChangeHandover };



// WEBPACK FOOTER //
// ./src/components/calendar2/changeDisplayHelper.ts